// Package imports:
import React from 'react';
// Component imports:
import LmdTable from '../../../ui-elements/Table/LmdTable';
import Link from '../../../ui-elements/Link/Link';
import CalculatedColoredNumber from '../../../ui-elements/CalculatedColoredNumber/CalculatedColoredNumber';
import Avatar, { AvatarNames } from '../../../ui-elements/Avatar/Avatar';
import MiniGraph from '../../../ui-elements/MiniGraph/MiniGraph';
// Service imports:
import { convertToPercentage, displayQuantityShorthand, formatNumber } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
// Type imports:
import { IApiLmdSnapshot } from '../../../types/HlutabrefTypes';
import { IDefaultProps } from '../../../types/Types';

type IProps = IDefaultProps;

const FirstNorth: React.FC<IProps>= ({
    refreshRateMs,
    accessToken,
}) => {
    const [ firstNorth, prevFirstNorth ] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/keldan/exchange/FNIS/market/*/symbol/*/delayed_equities',
        accessToken,
        refreshRateMs
    );

    return (
        <div className='KCL_FirstNorth'>
            <LmdTable 
                apiData={firstNorth}
                columns={[{
                    title: 'Auðkenni',
                    dataAccessor: ({symbol}) => (
                        <div className="avatar-and-link avatar-text-link">
                            <a href={`/Markadir/Hlutabref/${symbol}`} aria-label={`Hlutabréf - ${symbol}`}>
                                {(symbol === null)
                                    ? null
                                    : (
                                        <Avatar
                                            size='sm'
                                            backgroundGrey
                                            name={symbol.toUpperCase() as AvatarNames}
                                        />
                                    )
                                }
                            </a>
                            <Link url={`/Markadir/Hlutabref/${symbol}`} linkSize='13'>
                                {symbol}
                            </Link>
                        </div>
                    ),
                    textAlign: 'left',
                    simpleSortable: ({symbol}) => symbol
                }, {
                    title: 'Verð',
                    dataAccessor: (datum) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber 
                                    hasBackground
                                    currentValue={datum.last_price}
                                    previousValue={prevFirstNorth.data?.find(pd => pd.symbol === datum.symbol)?.last_price ?? null}
                                >
                                    {formatNumber(datum.last_price)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({last_price}) => last_price
                }, {
                    title: 'Breyting',
                    dataAccessor: (datum) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber
                                    hasColor
                                    hasBackground
                                    currentValue={datum.intraday_per_change}
                                    previousValue={prevFirstNorth.data?.find(pd => pd.symbol === datum.symbol)?.intraday_per_change ?? null}
                                >
                                    {convertToPercentage(datum.intraday_per_change, true)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({intraday_per_change}) => intraday_per_change
                }, {
                    title: 'Sl. 3 Mán',
                    dataAccessor: ({symbol}) => (symbol) && <MiniGraph symbol={symbol} />
                }, {
                    title: 'Kaup',
                    dataAccessor: (datum) => {
                        return (
                            <td style={{ padding: 0 }}>
                                <CalculatedColoredNumber
                                    hasBackground
                                    currentValue={datum.bid}
                                    previousValue={prevFirstNorth.data?.find(pd => pd.symbol === datum.symbol)?.bid ?? null}
                                >
                                    {formatNumber(datum.bid)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({bid}) => bid
                }, {
                    title: 'Sala',
                    dataAccessor: (datum) => {
                        return (
                            <td style={{ padding: 0}}>
                                <CalculatedColoredNumber
                                    hasBackground
                                    currentValue={datum.ask}
                                    previousValue={prevFirstNorth.data?.find(pd => pd.symbol === datum.symbol)?.ask ?? null}
                                >
                                    {formatNumber(datum.ask)}
                                </CalculatedColoredNumber>
                            </td>
                        )
                    },
                    overrideTd: true,
                    simpleSortable: ({ask}) => ask
                }, {
                    title: 'Velta',
                    dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                    simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
                }]}
            />
        </div>
    );
}

export default FirstNorth;